import { useEffect, useState } from "react";
import { Modal } from "../common/Modal";
import { Button } from "../common/Button";
import { Badge } from "../common/Badge";
import { useFeatureTheme } from "../../context/FeatureThemeContext";
import { useSubscription } from "../../context/SubscriptionContext";
import { useToast } from "../../context/ToastContext";
import { BILLING_ENABLED, BILLING_DISABLED_NOTICE, FOUNDING_OFFER_LIVE,
         FOUNDING_TAGLINE, planChangeVerb } from "../../config/billing";
import styles from "./UpgradeModal.module.css";

/**
 * The plan chooser (#243).
 *
 * This modal used to offer ONE destination -- Business -- with a hardcoded
 * two-column table that said "3 hives" long after Free became five. Its own
 * comment admitted the problem: "with two paid tiers this single CTA is no
 * longer really honest -- most users who hit it want Harvester."
 *
 * So: every plan, read from the API. Prices and feature lists live in
 * services/subscription.py _PLANS and NOWHERE ELSE -- this file hardcoded
 * $7/mo while the API said $9.99, and both were wrong once #186 settled on
 * $5.99 and $12.99. A table that drifts from the thing that charges people
 * is worse than no table.
 *
 * THEME IS NOT TIER. Buying a plan does not switch features on: the theme
 * decides what is enabled, and Advanced (every flag at once) is refused
 * below the top tier by design, because it would turn on Business features
 * a Harvester user has not bought. An upgrade switches on what the new
 * plan includes and changes NO theme (#561): the nudge then offers the
 * presets the plan opened, and the choice is the person's.
 */
/* Yearly first: it is the default, and order is the recommendation. */
const PERIODS = [["annual", "Yearly"], ["monthly", "Monthly"]];

export function UpgradeModal({ open, onClose, highlightFeature }) {
  const { refresh: refreshTheme, tier: currentTier } = useFeatureTheme();
  const { refresh: refreshSubscription } = useSubscription();
  const { notify } = useToast();
  const [busy, setBusy] = useState(null);
  const [plans, setPlans] = useState([]);
  const [failed, setFailed] = useState(false);
  /*
   * ANNUAL FIRST, MONTHLY ONE CLICK AWAY (#330: "Offer monthly, present
   * annual").
   *
   * The reason is seasonal, not financial. Beekeeping goes quiet from
   * November to February, and a monthly plan asks a user to make a
   * cancel-or-keep decision during exactly the months they are not
   * opening a hive. An annual member never has that moment, and faces
   * one card expiry a year instead of twelve -- the failure that #330
   * identified as the most likely way somebody loses a founding price
   * without ever choosing to leave.
   *
   * Defaulted, not forced. Monthly stays visible and one press away,
   * because a plan chooser that hides the cheaper-to-start option is
   * making the decision for the user.
   */
  const [period, setPeriod] = useState("annual");
  const [annualMonths, setAnnualMonths] = useState(12);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    import("../../services/userApi")
      .then(({ subscriptionApi }) => subscriptionApi.plans())
      .then((r) => {
        if (cancelled) return;
        setPlans(r.plans || []);
        // Sent by the API so the client never has to know a year is
        // twelve months. If that becomes 10 -- the familiar "two months
        // free" -- the saving shows up here with no web deploy.
        if (r.annual_months_charged) setAnnualMonths(Number(r.annual_months_charged));
      })
      // Never invent prices as a fallback: a wrong number here is a number
      // somebody makes a purchase decision on.
      .catch(() => !cancelled && setFailed(true));
    return () => { cancelled = true; };
  }, [open]);

  async function choose(plan) {
    setBusy(plan.tier);
    try {
      const { subscriptionApi } = await import("../../services/userApi");
      await subscriptionApi.update(plan.tier);

      /* NO THEME IS APPLIED (#561). Business used to switch the account to
         Advanced here, so the next sign-in showed a theme nobody had chosen,
         and choosing a theme records the nudge as answered -- which is why
         the Business presets were never offered. The plan change itself
         switches on what the new tier includes (sync_features_to_tier); which
         theme to wear is the person's choice, offered by the nudge. */
      await Promise.all([refreshTheme(), refreshSubscription()]);
      notify(
        plan.tier === "Business"
          ? `${plan.tier} plan active. Pick how you want it set up under Settings › Theme.`
          : `${plan.tier} plan active. Switch on what you want under Settings › Theme.`,
        "success",
      );
      onClose();
    } catch (err) {
      notify(err.apiMessage || `${planChangeVerb()} failed`, "danger");
    } finally {
      setBusy(null);
    }
  }

  const annual = period === "annual";
  const money = (n) => `$${Number(n).toFixed(2)}`;

  /* The figure charged, for the period being shown. */
  const listOf = (plan) =>
    Number(annual ? plan.price_annual_usd : plan.price_monthly_usd);
  const foundingOfPeriod = (plan) =>
    Number(annual ? plan.founding_price_annual_usd : plan.founding_price_monthly_usd);

  const priceOf = (plan) =>
    Number(plan.price_monthly_usd) === 0
      ? "Free"
      : `${money(listOf(plan))}${annual ? "/year" : "/mo"}`;

  /*
   * "$59.88/year" is the number charged; "$4.99/mo, billed yearly" is the
   * number a user can compare. Both, always -- people price-compare
   * software per month, and asking somebody to divide by twelve to check
   * they are not being overcharged is a decision made harder for no
   * reason.
   *
   * Only a real saving is ever claimed. At twelve months charged there
   * is none, and the honest pitch is one payment a year rather than an
   * invented discount.
   */
  const perMonthNote = (plan) => {
    if (!annual || Number(plan.price_monthly_usd) === 0) return null;
    const monthly = showFounding(plan)
      ? Number(plan.founding_price_monthly_usd)
      : Number(plan.price_monthly_usd);
    /*
     * Against what the user would ACTUALLY otherwise pay -- their own
     * monthly rate times twelve, not the list rate. A founding member
     * paying monthly spends $59.88 a year, so their saving is $9.98, not
     * the $11.98 a full-price customer saves. Comparing to the list
     * price would overstate it, which is the sort of number people
     * check.
     */
    const charged = showFounding(plan) ? foundingOfPeriod(plan) : listOf(plan);
    const saved = monthly * 12 - charged;
    return saved > 0
      ? `${money(monthly)}/mo, billed yearly — you save ${money(saved)}`
      : `${money(monthly)}/mo, billed yearly`;
  };

  /*
   * Founding pricing (#330), shown only when the offer is actually
   * running. The API sends founding_price_monthly_usd on every plan
   * regardless -- whether anybody is SHOWN it is a separate decision,
   * because a founding price is not a price, it is a three-year
   * commitment, and advertising one from a build that cannot charge
   * anybody is a promise nothing can keep.
   *
   * `showFounding` is a function rather than a constant so a plan with
   * no discount (Free) never renders a struck-through $0.
   */
  const foundingOf = (plan) => Number(plan.founding_price_monthly_usd);
  const showFounding = (plan) =>
    FOUNDING_OFFER_LIVE
    && Number(plan.price_monthly_usd) > 0
    && foundingOf(plan) > 0
    && foundingOf(plan) < Number(plan.price_monthly_usd);

  return (
    <Modal open={open} onClose={onClose} title="Choose a plan" size="lg"
      footer={<Button variant="secondary" onClick={onClose}>Maybe later</Button>}>

      {!BILLING_ENABLED && (
        <p className={styles.testingNotice} role="status">
          <strong>Testing build.</strong> {BILLING_DISABLED_NOTICE}
        </p>
      )}

      {/* BEFORE the prices, not after. A toggle underneath the numbers
          it changes is one a user finds only once they have already read
          the wrong ones.

          A real radiogroup, which carries obligations a styled pair of
          buttons does not: ONE tab stop for the group, arrow keys moving
          between the options, and aria-checked so a screen reader
          announces which period is selected rather than just reading two
          words. role="radio" without the key handling looks correct and
          traps a keyboard user on the default -- which, since the
          default is the more expensive single payment, is the worst
          option to strand somebody on. */}
      <div className={styles.periodToggle} role="radiogroup" aria-label="Billing period">
        {PERIODS.map(([value, label], i) => (
          <button
            key={value}
            type="button"
            role="radio"
            aria-checked={period === value}
            tabIndex={period === value ? 0 : -1}
            className={period === value ? styles.periodOn : styles.periodOff}
            onClick={() => setPeriod(value)}
            onKeyDown={(e) => {
              const step = e.key === "ArrowRight" || e.key === "ArrowDown" ? 1
                : e.key === "ArrowLeft" || e.key === "ArrowUp" ? -1 : 0;
              if (!step) return;
              e.preventDefault();
              const next = PERIODS[(i + step + PERIODS.length) % PERIODS.length];
              setPeriod(next[0]);
              // Focus follows selection in a radiogroup, so the next
              // arrow press moves on from where the user actually is.
              e.currentTarget.parentNode
                .querySelector(`[data-period="${next[0]}"]`)?.focus();
            }}
            data-period={value}
          >
            {label}
          </button>
        ))}
      </div>
      {annual && (
        /* Shown whether or not there is a discount. This was briefly
           gated on "no saving to show instead", which had it backwards:
           the money is the smaller half of the argument. What a year
           actually buys a beekeeper is not having to decide anything in
           November -- and #330 found the likeliest way somebody loses a
           founding price is not choosing to leave at all, it is a card
           expiring mid-winter. */
        <p className={styles.periodNote}>
          {annualMonths < 12
            ? `${12 - annualMonths} months free — and nothing to renew during the winter.`
            : "One payment a year — nothing to renew during the winter."}
        </p>
      )}

      {highlightFeature && (
        <p className={styles.highlight}>
          Looking for <strong>{highlightFeature}</strong>? The plan that includes it is marked below.
        </p>
      )}

      {failed && <p role="alert">Couldn't load the plans just now. Check your connection and reopen this.</p>}

      <div className={styles.plans}>
        {plans.map((plan) => {
          const isCurrent = plan.tier === currentTier;
          return (
            <div key={plan.tier} className={styles.plan}>
              <div className={styles.planHead}>
                <strong>{plan.tier}</strong>
                {isCurrent && <Badge tone="honey">Your plan</Badge>}
              </div>
              {showFounding(plan) ? (
                <>
                  <div className={styles.planPrice}>
                    {money(foundingOfPeriod(plan))}{annual ? "/year" : "/mo"}
                    {/* The list price stays visible and struck through.
                        A discount nobody can see the original of is just
                        a price, and it is the comparison that makes the
                        offer worth anything. */}
                    <span className={styles.planPriceWas}>
                      {money(listOf(plan))}
                    </span>
                  </div>
                  {perMonthNote(plan) && (
                    <div className={styles.planPriceSub}>{perMonthNote(plan)}</div>
                  )}
                  <div className={styles.foundingNote}>{FOUNDING_TAGLINE}</div>
                </>
              ) : (
                <>
                  <div className={styles.planPrice}>{priceOf(plan)}</div>
                  {perMonthNote(plan) && (
                    <div className={styles.planPriceSub}>{perMonthNote(plan)}</div>
                  )}
                </>
              )}
              <div className={styles.planLimit}>
                {plan.hive_limit ? `Up to ${plan.hive_limit} hives` : "Unlimited hives"}
              </div>
              <ul className={styles.planFeatures}>
                {(plan.features || []).map((f) => <li key={f}>{f}</li>)}
              </ul>
              {/* No button on the plan you already hold, and none on Free:
                  leaving a paid plan is a cancellation, which belongs in
                  Settings with whatever it has to say about billing -- not
                  behind a button labelled the same as an upgrade. */}
              {!isCurrent && Number(plan.price_monthly_usd) > 0 && (
                <Button onClick={() => choose(plan)} disabled={busy !== null}>
                  {busy === plan.tier ? `${planChangeVerb()}ing…` : `${planChangeVerb()} to ${plan.tier}`}
                </Button>
              )}
            </div>
          );
        })}
      </div>
    </Modal>
  );
}
